import React, { useEffect, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { Search } from "lucide-react";
import { FaEdit, FaEye, FaTrashAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ConfirmModal from "../../components/ConfirmModal";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import ScaleLoader from "react-spinners/ScaleLoader";

const BankType = () => {
  const navigate = useNavigate();

  /* ---------------- STATE ---------------- */
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const limit = 9999999;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /* ---------------- FETCH BANK LIST ---------------- */
  const fetchBanks = async () => {
    setLoading(true);

    const body = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      page,
      limit,
    };

    try {
      const res = await simplePostCall(
        ApiConfig.bankList,
        JSON.stringify(body)
      );

      if (res?.result) {
        setBanks(res.data || []);
      } else {
        setBanks([]);
        toast.error(res?.message || "Failed to load bank list");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, [page]);

  /* ---------------- DELETE ---------------- */
  const handleDeleteIncome = async () => {
    setIsDeleting(true);

    const body = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      id: deleteId,
    };

    try {
      const res = await simplePostCall(
        ApiConfig.bankDelete,
        JSON.stringify(body)
      );

      if (res?.result) {
        toast.success(res.message);
        fetchBanks();
      } else {
        toast.error(res?.message || "Delete failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  /* ---------------- UI (UNCHANGED) ---------------- */
  return (
    <div className="income-type-container">
      {/* Top Header */}
      <div
        className="top-header-section d-flex justify-content-between align-items-center"
        style={{ height: "55px" }}
      >
        <h1 className="page-title-text m-0">Bank Details</h1>

        <div className="action-buttons-group d-flex gap-2">
          <Button
            className="create-new-button"
            onClick={() => navigate("/accountant/CreateBank")}
          >
            Create Bank Details
          </Button>
          <Button className="export-data-button">Export</Button>
        </div>
      </div>

      {/* Content Section */}
      <div className="content-table-section">
        {/* Filter Inputs (UI unchanged) */}
        {/* <div className="expdash-filter-section">
          <div
            className="expdash-filter-inputs"
            style={{ height: "36px", width: "500px", marginBottom: "10px" }}
          >
            <div style={{ position: "relative" }}>
              <Search
                size={14}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "2px",
                  transform: "translateY(-50%)",
                  color: "#838080ff",
                }}
              />
              <Form.Control
                type="text"
                placeholder="Filter by Bank Name"
                className="expdash-filter-field"
                style={{ paddingLeft: "32px" }}
              />
            </div>

            <div style={{ position: "relative" }}>
              <Search
                size={14}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "2px",
                  transform: "translateY(-50%)",
                  color: "#878080ff",
                }}
              />
              <Form.Control
                type="text"
                placeholder="Filter by Account Holder"
                className="expdash-filter-field"
                style={{ paddingLeft: "32px" }}
              />
            </div>
          </div>
        </div> */}

        {/* Table Header */}
        <div
          className="table-headers-row d-flex align-items-center fw-semibold"
          style={{
            textAlign: "center",
            height: "55px",
            borderBottom: "1px solid #e5e5e5",
            padding: "0 12px",
          }}
        >
          <div style={{ width: "60px" }}>Sr.No</div>
          <div style={{ flex: 1 }}>Bank Name</div>
          <div style={{ flex: 1 }}>Account Holder Name</div>
          <div style={{ width: "140px" }}>Actions</div>
        </div>

        {/* Table Body */}
        <div className="bank-details-table bg-white rounded">
          {loading ? (
            <div className="text-center py-2">
               <ScaleLoader color="#F4BB44" />
            </div>
          ) : banks.length === 0 ? (
            <div className="text-center py-4">No data found</div>
          ) : (
            banks.map((bank, index) => (
              <div key={bank.bank_id} className="border-bottom py-2">
                <div className="row align-items-center text-center small">
                  <div className="col-1">{index + 1}</div>
                  <div className="col-4">{bank.bank_name}</div>
                  <div className="col-4">
                    {bank.bank_account_holder_name}
                  </div>

                  <div className="col-3">
                    <div className="d-flex justify-content-end gap-3">
                      <button
                        className="btn p-1"
                        title="View"
                        onClick={() =>
                          navigate(`/accountant/ViewBank/${bank.bank_id}`)
                        }
                      >
                        <FaEye />
                      </button>

                      <button
                        className="btn p-1"
                        title="Edit"
                        onClick={() =>
                          navigate(`/accountant/EditBank/${bank.bank_id}`)
                        }
                      >
                        <FaEdit />
                      </button>

                      <button
                        className="btn p-1"
                        title="Delete"
                        onClick={() => {
                          setDeleteId(bank.bank_id);
                          setShowDeleteModal(true);
                        }}
                      >
                        <FaTrashAlt color="red" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Summary */}
        {/* <div className="summary-status-bar" style={{ padding: "10px" }}>
          Showing {banks.length} item(s)
        </div> */}
      </div>

      {/* DELETE CONFIRM MODAL */}
      <ConfirmModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        title="Delete Income"
        message="Are you sure you want to delete this income record?"
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleDeleteIncome}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default BankType;
